import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getIntegrationStatus } from '@/modules/common/api/integration-status';
import { FeatureMode } from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationStatusDescriptor } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';

import { getAvailableIntegrations } from '../api/integrations.api';
import { ClientIntegration } from '../types/client';

export interface UseClientIntegrationsParams {
  mode: FeatureMode;
  clientId?: string;
  rawIntegrations?: IntegrationInfoResponse[];
}

/**
 * Maps an integration status descriptor to a ClientIntegration.
 * Used when /unlinked returns empty in Create mode — the integration is connected but all
 * external clients are already linked to other clients in the app.
 * allClientsLinked is derived automatically by the mapper (empty clients + no selectedValue).
 */
const mapStatusDescriptorToClientIntegration = (
  descriptor: IntegrationStatusDescriptor
): ClientIntegration => {
  const service = descriptor.integration as IntegrationServices;
  return {
    id: service,
    type: service,
    icon: getIntegrationIcon(service),
    clients: [],
    selectedValue: null,
    allClientsLinked: true,
  };
};

/**
 * Hook to fetch available integrations for clients.
 * In Create mode, fetches only unlinked integrations. When the unlinked list is empty,
 * falls back to /api/integrations/status to show integrations that have all clients
 * already linked (with a disabled selector and informational tooltip).
 * In Edit/Read mode, merges unlinked integrations with selected ones from client data.
 */
export const useClientIntegrations = ({
  mode,
  clientId,
  rawIntegrations,
}: UseClientIntegrationsParams): UseQueryResult<ClientIntegration[], Error> => {
  return useQuery({
    queryKey: [
      'available-client-integrations',
      mode,
      clientId,
      (rawIntegrations ?? []).map(i => ({
        p: i.integrationProviderType,
        c: i.connectedIntegration?.id ?? null,
      })),
    ],
    queryFn: async () => {
      const selectedIntegrations = mode !== FeatureMode.Create ? rawIntegrations : undefined;
      const unlinked = await getAvailableIntegrations(selectedIntegrations);

      if (mode === FeatureMode.Create && unlinked.length === 0) {
        const statusData = await getIntegrationStatus();

        // Keep only integrations that support the CLIENT entity type
        const clientIntegrations = statusData.integrations.filter(descriptor =>
          descriptor.entities.some(entity => entity.entityType === EntityType.Client)
        );

        return clientIntegrations
          .map(mapStatusDescriptorToClientIntegration)
          .sort((a, b) => a.type.localeCompare(b.type));
      }

      return unlinked;
    },
  });
};
